/**
 * 🤖 Servicio de IA para InnovaTech Lab - Frontend
 * Conecta con el backend seguro para procesamiento de hipótesis
 */

/**
 * 🎯 Función principal para procesar hipótesis InnovaTech
 * Llama al backend donde está la API key de OpenAI de forma segura
 */
export async function processInnovaTechHypothesis(context) {
  console.log('[🎯 AI Service Frontend] Procesando hipótesis:', context);
  
  const { hypothesis, lab_id, step_id, session_id } = context;
  
  // 🚀 PRIMERA OPCIÓN: Usar el backend (recomendado)
  try {
    console.log('[🎯 AI Service] Llamando al backend...');
    
    const backendResponse = await fetch('http://localhost:3001/api/ai/process', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        hypothesis,
        lab_id,
        step_id,
        session_id
      })
    });
    
    if (backendResponse.ok) {
      const data = await backendResponse.json();
      console.log('[✅ AI Service] Backend respuesta exitosa:', data);
      
      // 🎯 Devolver respuesta completa del backend (contrato v1.0 compatible)
      return data;
    } else {
      throw new Error(`Backend error: ${backendResponse.status}`);
    }
  } catch (error) {
    console.warn('[⚠️ AI Service] Backend no disponible, usando fallback:', error.message);
  }
  
  // 🔄 FALLBACK: Respuestas locales cuando backend no disponible
  console.log('[🎯 AI Service] Usando sistema fallback local');
  
  // Dispatcher automático por lab_id y step_id
  const roleDispatcher = {
    'lab1': {
      'hipotesis_central': 'tutor',
      'contexto': 'tutor',
      'formulario_hipotesis': 'tutor'
    },
    'lab2': {
      'sparring': 'sparring_cliente'
    },
    'lab3': {
      'analisis': 'analista'
    },
    'lab4': {
      'evaluacion': 'evaluador'
    }
  };
  
  const role = roleDispatcher[lab_id]?.[step_id] || 'tutor';
  
  console.log(`[🎯 AI Dispatcher Fallback] Lab: ${lab_id}, Step: ${step_id} → Rol: ${role}`);
  
  // Generar respuesta fallback mejorada
  return {
    content: getEnhancedFallbackContent(role, { hipotesis: hypothesis, labId: lab_id, stepId: step_id }),
    role: role,
    source: 'fallback_local'
  };
}

/**
 * 📝 Contenido fallback mejorado para demo cuando backend no disponible
 */
function getEnhancedFallbackContent(role, context) {
  const { hipotesis } = context;
  
  switch (role) {
    case 'tutor':
      return `**🎯 Validación de Hipótesis Lean Startup**

**✅ Fortaleza:** Tu hipótesis sigue la estructura [Cliente] + [Problema] + [Razón].

**🔍 Mejora Crítica:** 
- ¿Es tu cliente ESPECÍFICO? (ej: "estudiantes de 4to año de Sistemas")
- ¿El problema es MEDIBLE? (ej: "pierden 3 horas semanales buscando")
- ¿La razón es OBSERVABLE? (ej: "porque no existe filtro por especialidad")

**🚀 Siguiente Paso:**
Diseña 3 preguntas para validar con entrevistas:
1. ¿Cómo resuelves [problema] actualmente?
2. ¿Cuánto tiempo/dinero gastas en eso?
3. ¿Qué pasaría si no existiera solución?

*Tu hipótesis registrada para el circuito Build-Measure-Learn.*`;

    case 'sparring_cliente':
      return "Mmm, interesante idea... pero ¿realmente pagaría por eso? Ya tengo formas de resolver mi problema. ¿Cuánto me costaría y cuánto tiempo me ahorraría exactamente?";

    case 'analista':
      return `**📊 Análisis Básico Disponible**

• **Hipótesis registrada:** ✅ Sistema operativo
• **Métricas IA:** ⏳ Reconectando...
• **Interpretación:** Procesar manualmente por ahora
• **Siguiente experimento:** Validar segmento con 10-15 entrevistas

*Funciones analíticas restaurándose - procede con opciones manuales.*`;

    case 'evaluador':
      return `**🎯 Evaluación de Progreso**

**Puntuación General:** 75/100
- **Claridad:** Hipótesis bien estructurada
- **Evidencia:** Requiere más validación
- **Técnico:** Sólido pero necesita métricas

**Recomendación:** Continuar con experimentos de validación

*Sistema de evaluación completo disponible próximamente.*`;

    default:
      return `**⚠️ Análisis de Hipótesis (Modo Demo)**

Tu entrada: "${hipotesis.substring(0, 50)}..."

**Recomendaciones inmediatas:**
• Especifica más tu segmento de cliente
• Cuantifica el problema identificado
• Prepara preguntas de validación

**Próximo paso:** Continuar con el circuito Build-Measure-Learn

*Sistema de IA completo disponible próximamente.*`;
  }
}

/**
 * 🧪 Función de test para verificar conectividad
 */
/**
 * 🎯 Función principal getAIResponse para compatibilidad
 * Usa los prompts centralizados del JSON backend
 */
export async function getAIResponse(role, context) {
  console.log(`[🎯 AI Service] getAIResponse - Role: ${role}`, context);
  
  // Los prompts están centralizados en prompt_inovatech.json del backend
  return await processInnovaTechHypothesis({
    hypothesis: context.hipotesis,
    lab_id: context.labId || 'lab1',
    step_id: context.stepId,
    session_id: context.sessionId,
    role: role
  });
}

export async function testAIConnection() {
  try {
    const response = await fetch('http://localhost:3001/api/ai/status');
    
    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        backend_available: true,
        ...data
      };
    }
    
    return {
      success: false,
      backend_available: false,
      message: 'Backend no disponible'
    };
  } catch (error) {
    return {
      success: false,
      backend_available: false,
      error: error.message
    };
  }
}